/**
 * The settlement registry: how a confirmed value reaches the documents rendering it.
 *
 * A `setFields` names its own entity and applies wherever that id appears, so a producer cannot
 * know which documents will need the confirmed value — an issue title edited in a side panel has to
 * reach the panel, the dashboard row behind it, and anything else on the page holding that id.
 * Documents register here through `useOptimisticData`, and both producer kinds notify it: a native
 * `useEntityMutation` directly, and a Relay mutation through the entity-fields mirror.
 *
 * A module-level registry rather than React context, because the pairing is not a tree: the issue
 * viewer's header publishes an operation the issues dashboard behind it has to settle, and a
 * provider spanning both would have to be hoisted above every pairing anyone might build.
 */
import type {ConnectionOperation, SetFieldsOperation} from './connection-operations'

/** The `useMutation` variables an entity mutation stores, read back by `useOptimisticData`. */
export type EntityMutationVariables = {operation: SetFieldsOperation}

/**
 * A document's settle. Receives the owner and connection path the operation was published on, so a
 * consumer can decide for itself whether it cares: a `setFields` is judged by whether the document
 * contains the entity, a structural operation by whether the document renders that connection.
 */
type SettlementHandler = (operation: ConnectionOperation, ownerId: string, connectionPath: string[]) => Promise<void>

/**
 * Documents currently accepting confirmed entity operations. Module-level rather than a context
 * because the producer and the consumers are routinely in different React subtrees — the issue
 * viewer's header publishes an operation the issues dashboard behind it has to settle — and a
 * provider spanning both would have to be hoisted above every pairing anyone might build.
 *
 * This is the same reach `useEntityFieldsBridge` gets from a DOM event target, without the
 * round-trip through an event.
 */
export const settlementHandlers = new Set<SettlementHandler>()

/**
 * Folds `operation` into every registered document. Awaited by the producer, so no consumer's
 * overlay is dropped before its own `base` reflects the write.
 *
 * Drains rather than notifying a single snapshot: the settle is awaited and can span a real
 * refetch, which is long enough for another document to mount — a side panel opening over a list,
 * or a row rendering. That document overlays the still-pending operation, so it shows the new
 * value, and would revert the moment the overlay dropped if its own cache never received the fold.
 * A cache that never refetches on its own (`staleTime: Infinity`, as the issues dashboard's per-row
 * nodes use) would stay wrong indefinitely.
 *
 * Terminates because each handler is notified at most once: the loop only picks up handlers that
 * are registered *and* not yet notified, and a handler that unregisters mid-settle is simply never
 * added.
 */
export async function notifySettlement(
  operation: ConnectionOperation,
  ownerId: string,
  connectionPath: string[],
): Promise<void> {
  const notified = new Set<SettlementHandler>()

  while (true) {
    const pending = [...settlementHandlers].filter(handler => !notified.has(handler))
    if (pending.length === 0) return

    for (const handler of pending) notified.add(handler)
    await Promise.all(
      pending.map(async handler => {
        try {
          await handler(operation, ownerId, connectionPath)
        } catch {
          // One document failing to settle must not fail the others, and must not fail the
          // mutation: the server accepted the write, so reporting an error to the caller would
          // show a failure toast for a change that succeeded. That document's overlay drops onto
          // an unreconciled base and converges on its next read of server truth.
        }
      }),
    )
  }
}
